import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import {
  platformService,
  type InvitationCodeSignup,
  type PlatformInvitationCode,
} from '@/services/api/platform.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PlatformLayout } from '@/components/platform/PlatformLayout';
import { InvitationCodeStatus } from '@/components/platform/InvitationCodeStatus';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** One invitation code and every organization that signed up with it. */
export default function PlatformInvitationCodeDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const [, setLocation] = useLocation();
  const { startImpersonation } = useAuth();

  const [code, setCode] = useState<PlatformInvitationCode | null>(null);
  const [signups, setSignups] = useState<InvitationCodeSignup[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyOwnerId, setBusyOwnerId] = useState<number | null>(null);

  const handleError = useCallback((err: any, fallback: string) => {
    if (err?.response?.status === 401) {
      setLocation('/platform/login');
      return;
    }
    setError(err?.response?.data?.error || fallback);
  }, [setLocation]);

  useEffect(() => {
    platformService.getInvitationCode(id).then(setCode).catch((err) => handleError(err, 'Could not load the code'));
  }, [id, handleError]);

  useEffect(() => {
    setLoading(true);
    platformService
      .listInvitationCodeSignups(id, page)
      .then((result) => {
        setSignups(result.signups);
        setTotalPages(result.totalPages);
        setTotalCount(result.totalCount);
      })
      .catch((err) => handleError(err, 'Could not load signups'))
      .finally(() => setLoading(false));
  }, [id, page, handleError]);

  const handleImpersonate = async (ownerId: number) => {
    setBusyOwnerId(ownerId);
    try {
      await startImpersonation(ownerId);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to impersonate');
      setBusyOwnerId(null);
    }
  };

  return (
    <PlatformLayout title={code ? code.code : 'Invitation code'} wide>
      <Link href="/platform/invitation-codes" className="mb-4 inline-block text-sm text-brand-700 hover:underline">
        ← All invitation codes
      </Link>

      {error && <div role="alert" className="mb-4 rounded bg-danger-soft px-3 py-2 text-sm text-danger-text">{error}</div>}

      {code && (
        <dl className="mb-6 grid grid-cols-2 gap-4 rounded-2xl border border-line bg-surface p-5 text-sm shadow-card sm:grid-cols-4">
          <div><dt className="text-ink-subtle">Plan</dt><dd className="font-medium">{code.planTarget ?? '—'}</dd></div>
          <div><dt className="text-ink-subtle">Stripe coupon</dt><dd className="font-mono text-xs">{code.stripeCodeId}</dd></div>
          <div><dt className="text-ink-subtle">Expires</dt><dd>{formatDate(code.expiresAt)}</dd></div>
          <div>
            <dt className="text-ink-subtle">Granted / signed up</dt>
            <dd>{code.grantedCount} / {code.signupsCount}{code.maxSignups ? ` (max ${code.maxSignups})` : ''}</dd>
          </div>
          <div className="col-span-2 sm:col-span-4"><InvitationCodeStatus code={code} /></div>
          {code.notes && <div className="col-span-2 sm:col-span-4 text-ink-subtle">{code.notes}</div>}
        </dl>
      )}

      <h2 className="mb-2 text-base font-semibold text-ink">Signups ({totalCount})</h2>
      <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-ink-subtle">
            <tr>
              <th className="px-3 py-2">Organization</th>
              <th className="px-3 py-2">Owner</th>
              <th className="px-3 py-2">Signed up</th>
              <th className="px-3 py-2">Invited plan</th>
              <th className="px-3 py-2">Current subscription</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {signups.map((signup) => (
              <tr key={signup.organizationId} className="border-t border-line">
                <td className="px-3 py-2">{signup.organizationName}</td>
                <td className="px-3 py-2">{signup.ownerEmail ?? '—'}</td>
                <td className="px-3 py-2">{formatDate(signup.signedUpAt)}</td>
                <td className="px-3 py-2">
                  {signup.invitationCodeAppliedAt
                    ? <Badge tone="success">Granted</Badge>
                    : <Badge tone="warn">Fell back to trial</Badge>}
                </td>
                <td className="px-3 py-2">
                  {signup.subscriptionPlan ? `${signup.subscriptionPlan} · ${signup.subscriptionStatus}` : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {signup.ownerId && (
                    <Button
                      variant="outline"
                      disabled={busyOwnerId === signup.ownerId}
                      onClick={() => handleImpersonate(signup.ownerId!)}
                    >
                      {busyOwnerId === signup.ownerId ? 'Starting…' : 'Impersonate'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && signups.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-subtle">No signups with this code yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-ink-subtle">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </PlatformLayout>
  );
}
